import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pencil, Trash2, Eye } from 'lucide-react';
import {
  PLANNING_STATUSES,
  PLANNED_FORMATS,
  PRIORITIES,
  VIGENCIA_OPTIONS,
  type PlanningItem,
} from '@/hooks/useContentPlanning';

interface Props {
  items: PlanningItem[];
  onEdit: (item: PlanningItem) => void;
  onDelete: (item: PlanningItem) => void;
  onPreview?: (item: PlanningItem) => void;
}

export function ContentPlanningTable({ items, onEdit, onDelete, onPreview }: Props) {
  const statusMeta = (v: string) => PLANNING_STATUSES.find((s) => s.value === v);
  const formatLabel = (v: string | null) => PLANNED_FORMATS.find((f) => f.value === v)?.label;
  const priorityMeta = (v: string | null) => PRIORITIES.find((p) => p.value === v);
  const vigenciaMeta = (v: string | null) => VIGENCIA_OPTIONS.find((x) => x.value === (v ?? 'vigente'));

  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nombre</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Formato planificado</TableHead>
            <TableHead>Prioridad</TableHead>
            <TableHead>Responsable</TableHead>
            <TableHead>Ruta prospecto</TableHead>
            <TableHead>Vigencia</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const status = statusMeta(item.planning_status);
            const prio = priorityMeta(item.priority);
            return (
              <TableRow key={item.id}>
                <TableCell>
                  <div className="font-medium">{item.name}</div>
                  {item.category && (
                    <div className="text-xs text-muted-foreground">{item.category}</div>
                  )}
                </TableCell>
                <TableCell>
                  {status && <Badge variant="outline" className={status.color}>{status.label}</Badge>}
                </TableCell>
                <TableCell className="max-w-[260px]">
                  <span className="text-sm">{formatLabel(item.planned_format) ?? <span className="text-muted-foreground">—</span>}</span>
                </TableCell>
                <TableCell>
                  {prio ? <Badge variant="outline" className={prio.color}>{prio.label}</Badge> : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  <span className="text-sm">{item.responsible ?? <span className="text-muted-foreground">—</span>}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm">{item.prospect_route ?? <span className="text-muted-foreground">—</span>}</span>
                </TableCell>
                <TableCell>
                  {(() => {
                    const vig = vigenciaMeta(item.vigencia);
                    return vig ? <Badge variant="outline" className={vig.color}>{vig.label}</Badge> : <span className="text-muted-foreground">—</span>;
                  })()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {onPreview && (
                      <Button variant="ghost" size="icon" onClick={() => onPreview(item)} title="Previsualizar">
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => onEdit(item)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onDelete(item)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {items.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                No hay contenidos planificados con los filtros actuales.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
